<template>
  <div>
    <div class="tile">
      <div class="form-inline">
        <date-picker v-model="filterFormModel.period" type="month" value-type="YYYY-MM" format="MM/YYYY"
          :lang="$i18n.locale" :clearable="false" :placeholder="$t('selectMonth')" class="mr-1"
          @change="load"></date-picker>
        <button @click="load" class="btn btn-primary mr-1">{{ $t('search') }}</button>
        <button @click="resetScope" class="btn btn-secondary mr-1">Reset</button>
        <button @click="exportExcel" class="btn btn-primary mr-1" :disabled="!items.length && !details.length">
          <i class="fa fa-download"></i> {{ $t('exportExcel') }}
        </button>
      </div>
    </div>

    <div class="tile">
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <caption class="text-center text-uppercase font-weight-bold" style="caption-side: top; color: #000;">
            {{ $t('reportTitle') }} {{ monthLabel }}
          </caption>
          <thead>
            <tr>
              <th rowspan="2" class="align-middle">{{ $t('stt') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('fullName') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('region') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('position') }}</th>
              <th colspan="2" class="align-middle">{{ $t('kpi') }}</th>
              <th rowspan="2" class="align-middle">{{ $t('revenueBase') }}</th>
              <th colspan="5" class="align-middle">{{ $t('commission') }}</th>
            </tr>
            <tr>
              <th class="align-middle">{{ $t('kpiPercent') }}</th>
              <th class="align-middle">{{ $t('kpiBracket') }}</th>
              <th class="align-middle">{{ $t('commissionN') }}</th>
              <th class="align-middle">{{ $t('commissionNStar') }}</th>
              <th class="align-middle">{{ $t('commissionEAU') }}</th>
              <th class="align-middle">{{ $t('commissionCallFee') }}</th>
              <th class="text-success align-middle">{{ $t('total') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="region in regions">
              <tr v-for="(item, index) in region.employees" :key="item.userId">
                <td class="align-middle">{{ region.startIndex + index + 1 }}</td>
                <td class="text-left align-middle">{{ item.name }}</td>
                <td v-if="index === 0" :rowspan="region.employees.length" class="align-middle font-weight-bold">
                  {{ region.office }}
                </td>
                <td class="align-middle">{{ position(item.role) }}</td>
                <td class="align-middle" :style="kpiRateStyle(item.kpiPercent)">{{ percent(item.kpiPercent) }}</td>
                <td class="align-middle" :style="kpiRateStyle(item.kpiPercent)">{{ item.kpiBracket }}</td>
                <td class="text-right align-middle">{{ item.revenueBase | numFormat }}</td>
                <!-- Kèm tỷ lệ đang áp: chỉ có số tiền thì phải dò xuống bảng chính sách mới
                     biết đang được hưởng bao nhiêu phần trăm -->
                <td class="text-right align-middle">
                  {{ item.commissionN | numFormat }}
                  <div class="small text-muted">{{ ratePct(item.pctN) }}</div>
                </td>
                <td class="text-right align-middle">
                  {{ item.commissionNStar | numFormat }}
                  <div class="small text-muted">{{ ratePct(item.pctNStar) }}</div>
                </td>
                <td class="text-right align-middle">
                  {{ item.commissionEAU | numFormat }}
                  <div class="small text-muted"
                       v-b-tooltip.hover.top="rateHint(item.pctEAU, item.eauBase, item.pctBig, item.eauBigBase)">
                    {{ rateText(item.pctEAU, item.pctBig, item.eauBigBase) }}
                  </div>
                </td>
                <td class="text-right align-middle">
                  {{ item.commissionCallFee | numFormat }}
                  <div class="small text-muted"
                       v-b-tooltip.hover.top="rateHint(item.pctCallFee, item.callFeeBase, item.pctCallFeeBig, item.callFeeBigBase)">
                    {{ rateText(item.pctCallFee, item.pctCallFeeBig, item.callFeeBigBase) }}
                  </div>
                </td>
                <td class="text-right font-weight-bold text-success align-middle">
                  <span v-if="item.total > 0">{{ item.total | numFormat }}</span>
                  <span v-else>-</span>
                </td>
              </tr>
            </template>
            <tr v-if="items.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="4" class="text-left align-middle">{{ $t('grandTotal') }}</td>
              <td></td>
              <td></td>
              <td class="text-right align-middle">{{ mainTotal.revenueBase | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionN | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionNStar | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionEAU | numFormat }}</td>
              <td class="text-right align-middle">{{ mainTotal.commissionCallFee | numFormat }}</td>
              <td class="text-right text-success align-middle">{{ mainTotal.total | numFormat }}</td>
            </tr>
            <tr v-if="!items.length">
              <td colspan="12" class="text-danger">{{ $t('noData') }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tile">
      <h5 class="text-danger font-weight-bold mb-3">{{ $t('policyTitle') }}</h5>
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center">
          <thead>
            <tr>
              <th class="align-middle">{{ $t('kpi') }}</th>
              <th class="align-middle">CallioN ({{ $t('policySourceSelf') }})</th>
              <th class="align-middle">CallioN* ({{ $t('policySourceCompany') }})</th>
              <th class="align-middle">CallioE, CallioA, CallioU</th>
              <th class="align-middle">{{ $t('policyCallFee') }}</th>
              <th v-if="!isNewPolicy" class="align-middle">{{ $t('policyBig') }}</th>
              <th v-if="!isNewPolicy" class="align-middle">{{ $t('policyBigCallFee') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in policy" :key="row.kpi">
              <td class="font-weight-bold align-middle">{{ row.kpi }}</td>
              <td class="align-middle">{{ row.n }}</td>
              <td class="align-middle">{{ row.nStar }}</td>
              <td class="align-middle">{{ row.eau }}</td>
              <td class="align-middle">{{ row.callFee }}</td>
              <td v-if="!isNewPolicy" class="align-middle">{{ row.big }}</td>
              <td v-if="!isNewPolicy" class="align-middle">{{ row.bigCallFee }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="tile">
      <div class="form-inline mb-2">
        <b-form-input v-model="filterFormModel.q" :placeholder="$t('keywordPlaceholder')" class="mr-1 mb-1"
          @keyup.native.enter="load"></b-form-input>
        <client-select :model="filterFormModel" attribute="client" :isFilter="true" place-holder="selectClient"
          class="mr-1 mb-1"></client-select>
        <admin-user-select v-if="$store.state.user.role !== 'sales'" :model="filterFormModel" attribute="sales"
          role-filter="sales" :place-holder="$t('selectSales')" class="mr-1 mb-1"></admin-user-select>
        <select v-model="filterFormModel.source" class="form-control mr-1 mb-1">
          <option value="">{{ $t('allSource') }}</option>
          <option value="sales">{{ $t('sourceSales') }}</option>
          <option value="natural">{{ $t('sourceNatural') }}</option>
        </select>
        <select v-model="filterFormModel.loai" class="form-control mr-1 mb-1">
          <option value="">{{ $t('allType') }}</option>
          <option v-for="opt in loaiOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <select v-model="filterFormModel.plan" class="form-control mr-1 mb-1">
          <option value="">{{ $t('allPlan') }}</option>
          <option v-for="p in plans" :key="p.value" :value="p.value">{{ p.text }}</option>
        </select>
        <select v-model="filterFormModel.office" class="form-control mr-1 mb-1">
          <option value="">{{ $t('allRegion') }}</option>
          <option value="VP Miền Bắc">VP Miền Bắc</option>
          <option value="VP Miền Nam">VP Miền Nam</option>
        </select>
        <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
        <button @click="resetDetail" class="btn btn-secondary mr-1 mb-1">Reset</button>
      </div>
      <label class="text-uppercase font-weight-bold mb-0 mr-3" style="color: #000;">
        {{ $t('detailTitle') }} {{ monthLabel }}
      </label>
      <div class="table-responsive c-template-table">
        <table class="table table-hover table-bordered text-center commission-detail-table">
          <thead>
            <tr>
              <th class="align-middle">{{ $t('stt') }}</th>
              <th class="align-middle">{{ $t('dSales') }}</th>
              <th class="align-middle">{{ $t('region') }}</th>
              <th class="align-middle">{{ $t('dClientName') }}</th>
              <th class="align-middle">{{ $t('dDomain') }}</th>
              <th class="align-middle">{{ $t('dSource') }}</th>
              <th class="align-middle">{{ $t('dRevenueType') }}</th>
              <th class="align-middle">{{ $t('dPlanName') }}</th>
              <th class="align-middle">{{ $t('dUserPrice') }}</th>
              <th class="align-middle">{{ $t('dUserLimit') }}</th>
              <th class="align-middle">{{ $t('dExtraUser') }}</th>
              <th class="align-middle">{{ $t('dMonthCount') }}</th>
              <th class="align-middle">{{ $t('dConfirmTime') }}</th>
              <th class="align-middle">{{ $t('dOrderValue') }} <i class="fa fa-info-circle" v-b-tooltip.hover="{ placement: 'top', container: 'body', boundary: 'window' }" :title="$t('dOrderValueTooltip')"></i></th>
              <th class="align-middle">{{ $t('dAgencyPay') }}</th>
              <th class="align-middle">{{ $t('dRevenueBase') }}</th>
              <th class="align-middle">{{ $t('dKpiBracket') }}</th>
              <th class="align-middle">{{ $t('dCommissionPercent') }}</th>
              <th class="align-middle">{{ $t('dCommission') }}</th>
              <th class="align-middle">{{ $t('dNote') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in details" :key="index"
              :style="row.isReExtend ? 'background-color: #dff0d8;' : row.isBig ? 'background-color: #f2dede;' : ''">
              <td class="align-middle">{{ index + 1 }}</td>
              <td class="text-left align-middle">{{ row.sales }}</td>
              <td class="text-left align-middle">{{ row.office }}</td>
              <td class="text-left align-middle">{{ row.companyName }}</td>
              <td class="text-left align-middle">{{ row.domain }}</td>
              <td class="text-left align-middle">{{ row.source }}</td>
              <td class="text-left align-middle">{{ row.loai }}</td>
              <td class="text-left align-middle">{{ row.planName }}</td>
              <td class="text-right align-middle">{{ row.userPrice | numFormat }}</td>
              <td class="text-right align-middle">{{ row.userLimit | numFormat }}</td>
              <td class="text-right align-middle">{{ row.numberOfUserToBuy | numFormat }}</td>
              <td class="text-right align-middle">{{ row.monthCount }}</td>
              <td class="align-middle">{{ fmtTime(row.confirmTime) }}</td>
              <td class="text-right align-middle">{{ row.orderValue | numFormat }}</td>
              <td class="text-right align-middle">
                <span v-if="row.agencyCommission > 0">{{ row.agencyCommission | numFormat }}</span>
                <span v-else>-</span>
              </td>
              <td class="text-right align-middle">{{ row.revenueBase | numFormat }}</td>
              <td class="align-middle">{{ row.kpiBracket }}</td>
              <td class="text-right align-middle">{{ percent(row.commissionPercent) }}</td>
              <td class="text-right font-weight-bold align-middle">{{ row.commission | numFormat }}</td>
              <td class="text-left align-middle">{{ row.note }}</td>
            </tr>
            <tr v-if="!details.length">
              <td colspan="20" class="text-danger">{{ $t('noDetail') }}</td>
            </tr>
            <tr v-if="details.length" class="font-weight-bold" style="background-color: #fcf8e3;">
              <td colspan="13" class="text-left align-middle">{{ $t('grandTotal') }}</td>
              <td class="text-right align-middle">{{ detailTotal.orderValue | numFormat }}</td>
              <td class="text-right align-middle">{{ detailTotal.agencyCommission | numFormat }}</td>
              <td class="text-right align-middle">{{ detailTotal.revenueBase | numFormat }}</td>
              <td></td>
              <td></td>
              <td class="text-right text-danger align-middle">{{ detailTotal.commission | numFormat }}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import AdminUserSelect from '@/components/AdminUserSelect';
import ClientSelect from '@/components/ClientSelect';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/vi';

export default {
  components: { AdminUserSelect, ClientSelect, DatePicker },
  data() {
    return {
      items: [],
      details: [],
      loadedPeriod: null,   // kỳ của dữ liệu đang hiển thị (khác với kỳ đang chọn khi đang tải)
      plans: [],
      filterFormModel: this.defaultFilter(),
    }
  },
  computed: {
    monthLabel() {
      return moment(this.filterFormModel.period, 'YYYY-MM').format('MM/YYYY');
    },
    // Chính sách thu nhập mới theo QĐ 10/2026/QĐ-GADGET áp dụng từ kỳ 08/2026.
    // Kỳ trước đó vẫn hiển thị đúng bảng cũ để đối chiếu được với lương đã chi.
    isNewPolicy() {
      const p = moment(this.loadedPeriod || this.filterFormModel.period, 'YYYY-MM');
      return p.isValid() && (p.year() > 2026 || (p.year() === 2026 && p.month() + 1 >= 8));
    },
    policy() {
      if (this.isNewPolicy) {
        // Chính sách mới: một mức cước gọi duy nhất, không còn cột riêng cho khách >15 user
        return [
          { kpi: 'KPI<60%', n: '3.00%', nStar: '2.00%', eau: '2.00%', callFee: '0.50%' },
          { kpi: '60-100%', n: '6.00%', nStar: '3.00%', eau: '3.00%', callFee: '0.80%' },
          { kpi: '100-120%', n: '12.00%', nStar: '4.00%', eau: '4.00%', callFee: '0.90%' },
          { kpi: 'KPI>120%', n: '13.00%', nStar: '5.00%', eau: '5.00%', callFee: '1.00%' },
          { kpi: 'KPI>150%', n: '16.00%', nStar: '6.00%', eau: '6.00%', callFee: '1.00%' },
        ];
      }
      return [
        { kpi: '60-100%', n: '6.00%', nStar: '3.00%', eau: '3.00%', callFee: '0.80%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: '100-120%', n: '12.00%', nStar: '4.00%', eau: '4.00%', callFee: '0.90%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: 'KPI>120%', n: '13.00%', nStar: '5.00%', eau: '5.00%', callFee: '1.00%', big: '1.00%', bigCallFee: '0.50%' },
        { kpi: 'KPI>150%', n: '16.00%', nStar: '6.00%', eau: '6.00%', callFee: '1.00%', big: '1.00%', bigCallFee: '0.50%' },
      ];
    },
    regions() {
      // Gom theo khu vực (Phòng), trong khu vực sắp xếp theo tổng hoa hồng giảm dần
      let map = {};
      let order = [];
      this.items.forEach(item => {
        let office = item.office || this.$t('unknownRegion');
        if (!map[office]) {
          map[office] = { office, employees: [] };
          order.push(office);
        }
        map[office].employees.push(item);
      });
      order.forEach(office => {
        map[office].employees.sort((a, b) => b.total - a.total);
      });
      let regions = order.map(office => map[office]).sort((a, b) => a.office.localeCompare(b.office));
      let startIndex = 0;
      regions.forEach(region => {
        region.startIndex = startIndex;
        startIndex += region.employees.length;
      });
      return regions;
    },
    mainTotal() {
      return this.items.reduce((acc, item) => {
        acc.revenueBase += item.revenueBase || 0;
        acc.commissionN += item.commissionN || 0;
        acc.commissionNStar += item.commissionNStar || 0;
        acc.commissionEAU += item.commissionEAU || 0;
        acc.commissionCallFee += item.commissionCallFee || 0;
        acc.total += item.total || 0;
        return acc;
      }, { revenueBase: 0, commissionN: 0, commissionNStar: 0, commissionEAU: 0, commissionCallFee: 0, total: 0 });
    },
    detailTotal() {
      return this.details.reduce((acc, row) => {
        acc.orderValue += row.orderValue || 0;
        acc.agencyCommission += row.agencyCommission || 0;
        acc.revenueBase += row.revenueBase || 0;
        acc.commission += row.commission || 0;
        return acc;
      }, { orderValue: 0, agencyCommission: 0, revenueBase: 0, commission: 0 });
    },
    // Loại đơn tính hoa hồng (khớp đúng chuỗi loai backend trả về ở /commission-detail)
    loaiOptions() {
      return ['1. CallioN', '1*. CallioN*', '2. CallioE', '3. CallioA', '4. CallioU'];
    },
  },
  methods: {
    // Tỷ lệ hoa hồng đang áp cho nhóm đó. Dùng chung định dạng với cột %KPI và bảng chính sách
    // ngay bên dưới để người xem không phải đối chiếu hai kiểu số khác nhau.
    ratePct(v) {
      if (v === null || v === undefined) return '';
      return this.percent(v);
    },
    // Kỳ cũ áp hai tỷ lệ lên hai phần tiền khác nhau, nên hiện "2.00% · 1.00%" chứ KHÔNG dùng
    // dấu cộng — "2% + 1%" sẽ bị đọc thành cộng dồn 3%. Chỉ hiện tỷ lệ thứ hai khi nhân viên
    // thực sự có phần tiền thuộc nhóm đó, không thì mọi người đều thấy "+1%" dù không được hưởng.
    rateText(pct, bigPct, bigBase) {
      let text = this.ratePct(pct);
      if (bigPct && bigBase > 0) {
        text += ' · ' + this.ratePct(bigPct);
      }
      return text;
    },
    // Tooltip nói rõ tỷ lệ nào áp lên bao nhiêu tiền để đối chiếu tay ra đúng số
    rateHint(pct, base, bigPct, bigBase) {
      if (!(bigPct && bigBase > 0)) {
        return '';
      }
      let numFormat = this.$options.filters.numFormat;
      return this.$t('rateSplitHint', {
        pct: this.ratePct(pct),
        base: numFormat(base || 0),
        bigPct: this.ratePct(bigPct),
        bigBase: numFormat(bigBase),
      });
    },
    defaultFilter() {
      return { period: moment().format('YYYY-MM'), sales: null, client: null, q: '', source: '', loai: '', plan: '', office: '' };
    },
    load() {
      let f = this.filterFormModel;
      let [year, month] = f.period.split('-');
      let params = { month: parseInt(month), year: parseInt(year) };
      // Bảng chi tiết có bộ lọc riêng (sales/khách/từ khoá/nguồn/loại/gói/khu vực) — KHÔNG áp lên bảng hoa hồng chung
      let detailParams = Object.assign({}, params);
      if (f.sales) detailParams.sales = f.sales;
      if (f.client) detailParams.client = f.client;
      if (f.q) detailParams.q = f.q;
      if (f.source) detailParams.source = f.source;
      if (f.loai) detailParams.loai = f.loai;
      if (f.plan) detailParams.plan = f.plan;
      if (f.office) detailParams.office = f.office;
      let loader = this.$loading.show();
      Promise.all([
        this.$service.get('/report/commission', { params }),
        this.$service.get('/report/commission-detail', { params: detailParams }),
      ]).then(([commission, detail]) => {
        this.items = commission.data;
        this.details = detail.data;
      }).finally(() => {
        loader.hide();
      });
    },
    // Danh sách gói để lọc (giống màn Client): lấy từ /plan, lọc theo _id
    getPlans() {
      this.$bgService.get('/plan', { params: { page: 1, pageSize: 1000, status: 0 } }).then(response => {
        this.plans = response.data.docs.map(item => ({ value: item._id, text: item.name }));
      });
    },
    // Reset bộ lọc phạm vi báo cáo (tháng) — áp dụng cho cả 2 bảng
    resetScope() {
      this.filterFormModel.period = moment().format('YYYY-MM');
      this.load();
    },
    // Reset bộ lọc riêng của bảng chi tiết
    resetDetail() {
      Object.assign(this.filterFormModel, { sales: null, client: null, q: '', source: '', loai: '', plan: '', office: '' });
      this.load();
    },
    fmtTime(t) {
      return t ? moment(t).format('DD/MM/YYYY HH:mm:ss') : '';
    },
    percent(value) {
      return (value || 0).toFixed(2) + '%';
    },
    // Tô màu % đạt KPI: <60% đỏ, 60–100% vàng, 100–120% xanh lá, 120–150% xanh dương, >150% tím (giống bảng KPI)
    kpiRateStyle(percent) {
      if (percent === null || percent === undefined) {
        return {};
      }
      let color = '';
      if (percent < 60) {
        color = '#dc3545';
      } else if (percent < 100) {
        color = '#e0a800';
      } else if (percent <= 120) {
        color = '#28a745';
      } else if (percent <= 150) {
        color = '#007bff';
      } else if (percent > 150) {
        color = '#6f42c1';
      }
      return color ? { color, fontWeight: 'bold' } : {};
    },
    position(role) {
      const map = {
        supervisor: this.$t('roleSupervisor'),
        manager: this.$t('roleManager'),
      };
      return map[role] || this.$t('roleConsultant');
    },
    // Tô nền nguyên dòng (cả ô trống) trong phạm vi `count` cột - giống màu nền các bảng ở FE
    xlsxFillRow(row, argb, count) {
      for (let c = 1; c <= count; c++) {
        row.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
      }
    },
    // Kẻ viền + canh giữa cho toàn bộ vùng chữ nhật [r1..r2] x [c1..c2] (gồm cả ô trống)
    xlsxBorderRange(sheet, r1, r2, c1, c2) {
      let thin = { style: 'thin', color: { argb: 'FFBFBFBF' } };
      for (let r = r1; r <= r2; r++) {
        let row = sheet.getRow(r);
        for (let c = c1; c <= c2; c++) {
          let cell = row.getCell(c);
          cell.border = { top: thin, left: thin, bottom: thin, right: thin };
          cell.alignment = Object.assign({ vertical: 'middle' }, cell.alignment || {});
        }
      }
    },
    async exportExcel() {
      let money = '#,##0';
      let headerFill = 'FFF2F2F2';
      let totalFill = 'FFFCF8E3';
      let workbook = new Excel.Workbook();

      // Sheet 1: Bảng hoa hồng phòng kinh doanh
      let sheet = workbook.addWorksheet(this.$t('sheetCommission'));
      sheet.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('fullName'), key: 'name', width: 22 },
        { header: this.$t('region'), key: 'office', width: 16 },
        { header: this.$t('position'), key: 'position', width: 16 },
        { header: this.$t('kpiPercent'), key: 'kpiPercent', width: 12 },
        { header: this.$t('kpiBracket'), key: 'kpiBracket', width: 12 },
        { header: this.$t('revenueBase'), key: 'revenueBase', width: 18, style: { numFmt: money } },
        { header: this.$t('commissionN'), key: 'commissionN', width: 18, style: { numFmt: money } },
        { header: this.$t('rateCol'), key: 'pctN', width: 8 },
        { header: this.$t('commissionNStar'), key: 'commissionNStar', width: 18, style: { numFmt: money } },
        { header: this.$t('rateCol'), key: 'pctNStar', width: 8 },
        { header: this.$t('commissionEAU'), key: 'commissionEAU', width: 20, style: { numFmt: money } },
        { header: this.$t('rateCol'), key: 'pctEAU', width: 8 },
        { header: this.$t('commissionCallFee'), key: 'commissionCallFee', width: 18, style: { numFmt: money } },
        { header: this.$t('rateCol'), key: 'pctCallFee', width: 8 },
        { header: this.$t('total'), key: 'total', width: 18, style: { numFmt: money } },
      ];
      // Đếm cột theo khai báo ở trên: trước đây hardcode 12 nên vừa thêm cột là dòng tiêu đề,
      // dòng tổng và khung viền hụt mất mấy cột cuối
      let mainColCount = sheet.columns.length;
      this.regions.forEach(region => {
        region.employees.forEach((item, index) => {
          sheet.addRow({
            stt: region.startIndex + index + 1,
            name: item.name,
            office: region.office,
            position: this.position(item.role),
            kpiPercent: this.percent(item.kpiPercent),
            kpiBracket: item.kpiBracket,
            revenueBase: item.revenueBase || 0,
            commissionN: item.commissionN || 0,
            pctN: this.ratePct(item.pctN),
            pctNStar: this.ratePct(item.pctNStar),
            // Giữ y hệt cách hiện trên màn hình (gồm cả mức riêng khách >15 user của kỳ cũ),
            // tránh cảnh file tải về nói một đằng màn hình nói một nẻo
            pctEAU: this.rateText(item.pctEAU, item.pctBig, item.eauBigBase),
            pctCallFee: this.rateText(item.pctCallFee, item.pctCallFeeBig, item.callFeeBigBase),
            commissionNStar: item.commissionNStar || 0,
            commissionEAU: item.commissionEAU || 0,
            commissionCallFee: item.commissionCallFee || 0,
            total: item.total || 0,
          });
        });
      });
      if (this.items.length) {
        let totalRow = sheet.addRow({
          name: this.$t('grandTotal'),
          revenueBase: this.mainTotal.revenueBase,
          commissionN: this.mainTotal.commissionN,
          commissionNStar: this.mainTotal.commissionNStar,
          commissionEAU: this.mainTotal.commissionEAU,
          commissionCallFee: this.mainTotal.commissionCallFee,
          total: this.mainTotal.total,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, totalFill, mainColCount);
      }
      sheet.getRow(1).font = { bold: true };
      sheet.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(sheet.getRow(1), headerFill, mainColCount);
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      let mainLastRow = sheet.rowCount;
      this.xlsxBorderRange(sheet, 1, mainLastRow, 1, mainColCount);

      // Bảng chính sách hoa hồng (đặt dưới bảng hoa hồng, cách 1 dòng trống)
      sheet.addRow([]);
      let policyTitleRow = sheet.addRow([this.$t('policyTitle')]);
      policyTitleRow.font = { bold: true, color: { argb: 'FFC00000' } };
      // Từ kỳ 08/2026 bảng chính sách không còn 2 cột dành cho khách >15 user
      let policyCols = [
        this.$t('kpi'),
        'CallioN (' + this.$t('policySourceSelf') + ')',
        'CallioN* (' + this.$t('policySourceCompany') + ')',
        'CallioE, CallioA, CallioU',
        this.$t('policyCallFee'),
      ];
      if (!this.isNewPolicy) {
        policyCols.push(this.$t('policyBig'), this.$t('policyBigCallFee'));
      }
      let policyColCount = policyCols.length;
      let policyHeaderRow = sheet.addRow(policyCols);
      policyHeaderRow.font = { bold: true };
      policyHeaderRow.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(policyHeaderRow, headerFill, policyColCount);
      this.policy.forEach(row => {
        let cells = [row.kpi, row.n, row.nStar, row.eau, row.callFee];
        if (!this.isNewPolicy) cells.push(row.big, row.bigCallFee);
        let r = sheet.addRow(cells);
        r.getCell(1).font = { bold: true };
      });
      this.xlsxBorderRange(sheet, policyHeaderRow.number, sheet.rowCount, 1, policyColCount);

      // Sheet 2: Chi tiết đơn hàng được tính hoa hồng
      let detail = workbook.addWorksheet(this.$t('sheetDetail'));
      detail.columns = [
        { header: this.$t('stt'), key: 'stt', width: 6 },
        { header: this.$t('dSales'), key: 'sales', width: 20 },
        { header: this.$t('region'), key: 'office', width: 16 },
        { header: this.$t('dClientName'), key: 'companyName', width: 26 },
        { header: this.$t('dDomain'), key: 'domain', width: 22 },
        { header: this.$t('dSource'), key: 'source', width: 14 },
        { header: this.$t('dRevenueType'), key: 'loai', width: 14 },
        { header: this.$t('dPlanName'), key: 'planName', width: 12 },
        { header: this.$t('dUserPrice'), key: 'userPrice', width: 12, style: { numFmt: money } },
        { header: this.$t('dUserLimit'), key: 'userLimit', width: 12 },
        { header: this.$t('dExtraUser'), key: 'numberOfUserToBuy', width: 12 },
        { header: this.$t('dMonthCount'), key: 'monthCount', width: 10 },
        { header: this.$t('dConfirmTime'), key: 'confirmTime', width: 20 },
        { header: this.$t('dOrderValue'), key: 'orderValue', width: 18, style: { numFmt: money } },
        { header: this.$t('dAgencyPay'), key: 'agencyCommission', width: 16, style: { numFmt: money } },
        { header: this.$t('dRevenueBase'), key: 'revenueBase', width: 18, style: { numFmt: money } },
        { header: this.$t('dKpiBracket'), key: 'kpiBracket', width: 14 },
        { header: this.$t('dCommissionPercent'), key: 'commissionPercent', width: 14 },
        { header: this.$t('dCommission'), key: 'commission', width: 16, style: { numFmt: money } },
        { header: this.$t('dNote'), key: 'note', width: 32 },
      ];
      this.details.forEach((row, index) => {
        let r = detail.addRow({
          stt: index + 1,
          sales: row.sales,
          office: row.office,
          companyName: row.companyName,
          domain: row.domain,
          source: row.source,
          loai: row.loai,
          planName: row.planName,
          userPrice: row.userPrice || 0,
          userLimit: row.userLimit || 0,
          numberOfUserToBuy: row.numberOfUserToBuy || 0,
          monthCount: row.monthCount || 0,
          confirmTime: this.fmtTime(row.confirmTime),
          orderValue: row.orderValue || 0,
          agencyCommission: row.agencyCommission || 0,
          revenueBase: row.revenueBase || 0,
          kpiBracket: row.kpiBracket,
          commissionPercent: this.percent(row.commissionPercent),
          commission: row.commission || 0,
          note: row.note,
        });
        if (row.isReExtend) {
          this.xlsxFillRow(r, 'FFDFF0D8', 20);
        } else if (row.isBig) {
          this.xlsxFillRow(r, 'FFF2DEDE', 20);
        }
      });
      if (this.details.length) {
        let totalRow = detail.addRow({
          companyName: this.$t('grandTotal'),
          orderValue: this.detailTotal.orderValue,
          agencyCommission: this.detailTotal.agencyCommission,
          revenueBase: this.detailTotal.revenueBase,
          commission: this.detailTotal.commission,
        });
        totalRow.font = { bold: true, color: { argb: 'FFC00000' } };
        this.xlsxFillRow(totalRow, totalFill, 20);
      }
      detail.getRow(1).font = { bold: true };
      detail.getRow(1).alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
      this.xlsxFillRow(detail.getRow(1), headerFill, 20);
      detail.views = [{ state: 'frozen', ySplit: 1 }];
      this.xlsxBorderRange(detail, 1, detail.rowCount, 1, 20);

      let buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
        `hoa-hong-${this.filterFormModel.period}.xlsx`);
    }
  },
  mounted() {
    this.getPlans();
    this.load();
  },
  i18n: {
    messages: {
      en: {
        reportTitle: 'Sales commission month',
        selectSales: 'Select sales person',
        selectMonth: 'Select month',
        keywordPlaceholder: 'Keyword...',
        allSource: 'All sources',
        sourceSales: 'Sales self-sourced',
        sourceNatural: 'Organic',
        allType: 'All types',
        allPlan: 'All plans',
        allRegion: 'All regions',
        stt: 'No.',
        fullName: 'Full name',
        region: 'Department',
        position: 'Position',
        kpi: 'KPI',
        kpiPercent: 'KPI rate',
        kpiBracket: 'Bracket',
        revenueBase: 'Revenue for commission',
        commission: 'Commission',
        rateCol: 'Rate',
        commissionN: 'Revenue commission CallioN',
        commissionNStar: 'Revenue commission CallioN*',
        commissionEAU: 'Revenue commission CallioE, CallioA, CallioU',
        commissionCallFee: 'Call fee commission',
        total: 'Total commission',
        roleConsultant: 'Sales',
        roleSupervisor: 'Supervisor',
        roleManager: 'Manager',
        unknownRegion: 'Unknown',
        noData: 'No KPI data for this month',
        policyTitle: 'Commission policy',
        policyCallFee: 'Call fee',
        rateSplitHint: '{pct} on {base} + {bigPct} on {bigBase} (customers with >15 users)',
        policyBig: 'Customers with >15 users (Customers >= 12 months)',
        policyBigCallFee: 'Call fee of customers with >15 users (Customers > 12 months)',
        policySourceSelf: 'Self-sourced',
        policySourceCompany: 'Company source',
        detailTitle: 'Commissioned orders detail month',
        dSales: 'Sales in charge',
        dClientName: 'Client name',
        dDomain: 'Domain',
        dSource: 'Client source',
        dRevenueType: 'Revenue type',
        dPlanName: 'Plan',
        dUserPrice: 'Price/user',
        dUserLimit: 'Max users',
        dExtraUser: 'Extra users bought',
        dMonthCount: 'Months',
        dConfirmTime: 'Confirm time',
        dOrderValue: 'Order value',
        dOrderValueTooltip: 'The total value of the order after applying any customer discounts',
        dAgencyPay: 'Agency payment',
        dRevenueBase: 'Commissionable revenue',
        dKpiBracket: 'KPI bracket for commission',
        dCommissionPercent: 'Commission rate',
        dCommission: 'Commission received',
        dNote: 'Note',
        grandTotal: 'Grand total',
        exportExcel: 'Export Excel',
        sheetCommission: 'Commission',
        sheetDetail: 'Order detail',
        noDetail: 'No commissioned orders for this month',
      },
      vi: {
        reportTitle: 'Hoa hồng phòng kinh doanh tháng',
        selectSales: 'Chọn nhân viên kinh doanh',
        selectMonth: 'Chọn tháng',
        keywordPlaceholder: 'Từ khoá...',
        allSource: 'Tất cả nguồn',
        sourceSales: 'Tự tìm',
        sourceNatural: 'Tự nhiên',
        allType: 'Tất cả loại',
        allPlan: 'Tất cả gói',
        allRegion: 'Tất cả khu vực',
        stt: 'STT',
        fullName: 'Họ tên',
        region: 'Phòng',
        position: 'Chức vụ',
        kpi: 'KPI',
        kpiPercent: 'Tỷ lệ đạt',
        kpiBracket: 'Mức',
        revenueBase: 'Doanh thu để tính hoa hồng cho sales',
        commission: 'Hoa hồng',
        rateCol: 'Tỷ lệ',
        commissionN: 'Hoa hồng doanh thu CallioN',
        commissionNStar: 'Hoa hồng doanh thu CallioN*',
        commissionEAU: 'Hoa hồng doanh thu CallioE, CallioA, CallioU',
        commissionCallFee: 'Hoa hồng tiền cước thoại Callio',
        total: 'Tổng cộng hoa hồng',
        roleConsultant: 'Kinh doanh',
        roleSupervisor: 'Trưởng nhóm',
        roleManager: 'Quản lý',
        unknownRegion: 'Chưa xác định',
        noData: 'Chưa có dữ liệu KPI cho tháng này',
        policyTitle: 'Chính sách hoa hồng',
        policyCallFee: 'Cước gọi',
        rateSplitHint: '{pct} trên {base} đ + {bigPct} trên {bigBase} đ (khách trên 15 user)',
        policyBig: 'Khách hàng có số user >15 (Khách hàng >= 12 tháng)',
        policyBigCallFee: 'Cước gọi khách hàng có số user >15 (Khách hàng > 12 tháng)',
        policySourceSelf: 'Nguồn tự tìm',
        policySourceCompany: 'Nguồn công ty',
        detailTitle: 'Chi tiết đơn hàng tính hoa hồng tháng',
        dSales: 'Kinh doanh phụ trách',
        dClientName: 'Tên khách hàng',
        dDomain: 'Domain',
        dSource: 'Nguồn khách hàng',
        dRevenueType: 'Loại doanh thu',
        dPlanName: 'Tên gói',
        dUserPrice: 'Giá/user',
        dUserLimit: 'Số user tối đa',
        dExtraUser: 'Số user mua thêm',
        dMonthCount: 'Số tháng',
        dConfirmTime: 'Thời gian xác nhận',
        dOrderValue: 'Giá trị đơn hàng',
        dOrderValueTooltip: 'Giá trị đơn hàng sau khi trừ chi phí giảm giá cho khách hàng',
        dAgencyPay: 'Số tiền chi trả cho đại lý',
        dRevenueBase: 'Doanh thu tính hoa hồng',
        dKpiBracket: 'KPI tính hoa hồng',
        dCommissionPercent: '% Hoa hồng',
        dCommission: 'Hoa hồng được nhận',
        dNote: 'Ghi chú',
        grandTotal: 'Tổng cộng',
        exportExcel: 'Xuất dữ liệu',
        sheetCommission: 'Hoa hồng',
        sheetDetail: 'Chi tiết hoa hồng',
        noDetail: 'Chưa có đơn hàng được tính hoa hồng cho tháng này',
      }
    }
  }
}
</script>

<style scoped>
.c-template-table table.table thead th {
  background-color: #f2f2f2 !important;
}

.commission-detail-table th,
.commission-detail-table td {
  white-space: nowrap;
}
</style>
